import type { AiReviewLocation } from "./ai-review-groups";
import { notifyError } from "@/lib/notify-error";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { AiGeometry } from "@constructionautomation/backend/convex/takeoffPilot/aiMeasurementGeometry";
import { AiCountOverlay, AI_COUNTS_ENABLED } from "./ai-count-overlay";
import { Toggle } from "@/components/ui/toggle";
import type { AiBatchReview, AiCountPage, AiLatestReview } from "./ai-count-types";
import { Eye, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  MeasurementOverlay,
  type MeasurementTagOption,
  type MeasurementAnnotation,
  type DraftMeasurementState,
  type MeasurementOverlayRecord,
} from "@/viewport-tools/measurement/overlay";
import {
  CalibrationOverlay,
  MEASUREMENT_INCREMENT_OPTIONS,
  MEASUREMENT_TYPES,
  TAG_TOOL_IDS,
  TOOL_IDS,
  VIEWPORT_TOOLSET_PLACEMENTS,
  VIEWPORT_VIEW_IDS,
  type TagToolId,
  type ToolType,
  type MeasurementType,
} from "@/viewport-tools";
import {
  ViewportToolsetsHeader,
  ViewportToolsetsRail,
  getViewportToolsetsForPlacement,
} from "@/viewport-tools";
import { IconButton } from "@/components/ui/icon-button";
import { UndoRedoControls } from "@/components/undo-redo-controls";
import { UndoRedoBusyError } from "@/lib/undo-redo-error";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import type { DocumentViewportValue, DocumentSource } from "@/features/document-viewport/types";
import { DocumentTagEditorOverlay } from "@/features/document-intelligence/document-tag-editor-overlay";
import { useTagInstanceNavigation } from "@/features/takeoff-pilot/use-tag-instance-navigation";
import { useTakeoffViewerFocus } from "./use-takeoff-viewer-focus";
import { useAiReviewNavigation } from "@/features/takeoff-pilot/use-ai-review-navigation";
import { useAiDrawing, type AiDrawing } from "./use-ai-drawing";
import type { AiVersion } from "./ai-history-lineage";
import { getAiHistorySession } from "./ai-history-session";
import { MeasurementHistoryLineage } from "./measurement-history-lineage";
import { useTakeoffViewportToolsetControllers } from "@/features/takeoff-pilot/use-takeoff-viewport-toolset-controllers";
import { useSnapArtifactsAvailable } from "@/features/snapping/use-snap-artifacts-available";
import { useMeasurementScale } from "@/hooks/useMeasurementScale";
import { useTakeoffTextSettings } from "./use-takeoff-text-settings";
import { useMeasurementLabelGrouping } from "./use-measurement-label-grouping";
import { useTakeoffJobHistory, type TakeoffJobHistory } from "./use-takeoff-job-history";
import { useLoggedMutation, useRecordActionLog } from "@/hooks/use-action-log-session";
import { computeMeasurementValue, parseFeetInches, type Point } from "@/lib/geometry";
import { DEFAULT_TAG_COLOR } from "@/lib/tag-colors";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { TakeoffViewerHeader } from "@/features/takeoff-pilot/viewer-header";
import { useRevisionSheetLabels } from "@/features/sheet-index/use-revision-sheets";
import type { MeasurementBreakdownTargetRef, ViewerSource } from "@/types/takeoff-pilot";
import type { DocumentTagOption, DocumentTagPlacementMode } from "@/viewport-tools/tags";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

type DetectionRecord = {
  _id: Id<"documentIntelligenceDetections">;
  pageNumber: number;
  rawText: string;
  normalizedText: string;
  classification: "finish_tag" | "room_tag" | "door_window_tag" | "reference_noise" | "unknown";
  bbox: { x: number; y: number; width: number; height: number };
  isUserManaged?: boolean;
  createdByUserId?: Id<"users">;
};

const MIXED_MEASUREMENT_TYPE_ERROR =
  "Only one dimensional measurement type can be assigned to a tag";
const MEASUREMENT_INCREMENT_STORAGE_KEY = "takeoff-measurement-increment-snap";
const DEFAULT_INCREMENT_STEP_FEET = 1;
const VALID_MEASUREMENT_INCREMENT_STEPS = new Set(
  MEASUREMENT_INCREMENT_OPTIONS.map((option) => option.stepFeet)
);

function isHistoryControlBusy(isBusy: boolean, pendingEditCount: number) {
  return isBusy || pendingEditCount > 0;
}
function isHistoryMutationBlocked(isBusy: boolean, unavailable: boolean) {
  return isBusy || unavailable;
}


function hexToRgba(color: string, alpha: number) {
  const normalized = color.trim();
  const shortMatch = /^#([\da-f]{3})$/i.exec(normalized);
  if (shortMatch) {
    const [r, g, b] = shortMatch[1].split("").map((channel) => parseInt(channel + channel, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const longMatch = /^#([\da-f]{6})$/i.exec(normalized);
  if (!longMatch) {
    return undefined;
  }

  const int = parseInt(longMatch[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getMeasurementMutationErrorMessage(error: unknown, fallbackMessage: string) {
  const message = getUserFacingErrorMessage(error, fallbackMessage);
  const rawMessage = error instanceof Error ? error.message : message;

  if (rawMessage.includes(MIXED_MEASUREMENT_TYPE_ERROR)) {
    return "This tag already has a different measurement type. Keep LF, SQFT, and CF on separate tags.";
  }

  if (rawMessage.includes("Measurements can only be attached to selected tags")) {
    return "Choose a selected tag before saving or reassigning a measurement.";
  }

  return message;
}

function getRevisionPageDetectionQueryArgs(args: {
  revisionId: Id<"projectDocumentRevisions">;
  normalizedTexts: string[];
  pageNumber: number;
}) {
  if (args.normalizedTexts.length === 0) {
    return "skip" as const;
  }

  return {
    revisionId: args.revisionId,
    pageNumber: args.pageNumber,
    normalizedTexts: args.normalizedTexts,
  };
}

function getRevisionDetectionQueryArgs(args: {
  revisionId: Id<"projectDocumentRevisions">;
  normalizedTexts: string[];
}) {
  if (args.normalizedTexts.length === 0) {
    return "skip" as const;
  }

  return {
    revisionId: args.revisionId,
    normalizedTexts: args.normalizedTexts,
  };
}

export interface TakeoffViewerTag {
  isCustom?: boolean;
  tagId: Id<"takeoffPilotExtractedTags">;
  rawValue: string;
  normalizedValue: string;
  colorHex?: string;
}

export interface TakeoffResultsViewerHandle {
  focusNextInstanceForTag: (tagId: Id<"takeoffPilotExtractedTags"> | string) => void;
  focusPreviousInstanceForTag: (tagId: Id<"takeoffPilotExtractedTags"> | string) => void;
  focusMeasurementBreakdownRow: (args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => void;
}

interface TakeoffResultsViewerProps {
  jobHistory?: TakeoffJobHistory;
  documentScopeKey?: string;
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId: Id<"takeoffPilotPlanDocuments"> | null;
  planRevisionId: Id<"projectDocumentRevisions">;
  planFilename: string;
  planFileUrl: string | null;
  planProjectId: Id<"projects">;
  planSourceDocumentId: Id<"projectDocuments">;
  documentIntelligenceRunId: Id<"documentIntelligenceRuns"> | null;
  persistedTextLocked?: boolean;
  persistedTextFontSize?: number;
  tags: TakeoffViewerTag[];
  isViewOnly?: boolean;
  onNavigableTagIdsChange?: (tagIds: string[]) => void;
  viewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  onSelectPlanDocumentId: (value: string) => void;
  sharedPendingFocusRef?: React.RefObject<MeasurementBreakdownTargetRef | null>;
  /** Job name for the viewer top bar. */
  jobName: string;
  /** Top bar subtitle (linked schedule revision). */
  jobSubtitle?: string;
  aiReviewContainer?: HTMLDivElement | null;
  aiReviewOpen?: boolean;
  aiReviewLocation?: (AiReviewLocation & { sequence: number }) | null;
  onNavigateAiReview?: (location: AiReviewLocation) => void;
  onToggleAiReview?: () => void;
  onClose: () => void;
  /** Opens the shared Export dialog. */
  onExport: () => void;
  isExportInProgress: boolean;
  onCreateCustomLineItem?: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<Id<"takeoffPilotExtractedTags"> | void>;
  bottomPanelHeight?: number;
}

function getBottomPanelPanPadding(bottomPanelHeight: number | undefined) {
  // Pan clamping is centered on the available content rect, so a bottom-only
  // overlay changes the usable bottom edge by half its height. Double the
  // overlay height here to reserve the full covered area for panning.
  return (bottomPanelHeight ?? 0) * 2 + 28;
}

function getTagPlacementMode(activeTagTool: TagToolId | null): DocumentTagPlacementMode {
  if (activeTagTool === TAG_TOOL_IDS.add) {
    return "add";
  }
  if (activeTagTool === TAG_TOOL_IDS.duplicate) {
    return "duplicate";
  }
  return null;
}

function ViewportValueBinder({
  onViewportChange,
}: {
  onViewportChange: (viewport: DocumentViewportValue) => void;
}) {
  const viewport = useDocumentViewport();

  useEffect(() => {
    onViewportChange(viewport);
  }, [onViewportChange, viewport]);

  return null;
}

/** View toggle for the counted-tag overlays (first group in the viewer's tool strip). */
function CountedTagsToggle({
  showOverlays,
  overlaysAvailable,
  setShowOverlays,
}: {
  showOverlays: boolean;
  overlaysAvailable: boolean;
  setShowOverlays: (value: boolean) => void;
}) {
  if (!overlaysAvailable) {
    return (
      <IconButton
        label="Tag overlays unavailable for this document"
        icon={EyeOff}
        disabled
      />
    );
  }
  return (
    <IconButton
      label="Show counted tags"
      icon={showOverlays ? Eye : EyeOff}
      pressed={showOverlays}
      onClick={() => setShowOverlays(!showOverlays)}
    />
  );
}

export const TakeoffResultsViewer = forwardRef<
  TakeoffResultsViewerHandle,
  TakeoffResultsViewerProps
>(
// eslint-disable-next-line complexity
function TakeoffResultsViewer({
  jobHistory,
  documentScopeKey,
  jobId,
  planDocumentId,
  planRevisionId,
  planFilename,
  planFileUrl,
  planProjectId,
  planSourceDocumentId,
  documentIntelligenceRunId: _documentIntelligenceRunId,
  persistedTextLocked,
  persistedTextFontSize,
  tags,
  isViewOnly = false,
  onNavigableTagIdsChange,
  viewerSources,
  selectedPlanDocumentId,
  onSelectPlanDocumentId,
  sharedPendingFocusRef,
  jobName,
  jobSubtitle,
  aiReviewContainer = null,
  aiReviewOpen = false,
  aiReviewLocation,
  onNavigateAiReview,
  onToggleAiReview,
  onClose,
  onExport,
  isExportInProgress,
  onCreateCustomLineItem,
  bottomPanelHeight,
}, ref) {
  const aiReviewToggleRef = useRef<HTMLButtonElement>(null);
  const previouslyOpenAiReview = useRef(aiReviewOpen);
  useEffect(() => {
    if (previouslyOpenAiReview.current && !aiReviewOpen) aiReviewToggleRef.current?.focus();
    previouslyOpenAiReview.current = aiReviewOpen;
  }, [aiReviewOpen]);
  // Reviewers view measurements and tags but get no tools, history, or settings writes.
  const canEdit = useCanEdit();
  const showEditTools = !isViewOnly && canEdit;
  const [aiPlacement, setAiPlacement] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const { data: aiPageData } = useQuery(convexQuery(api.takeoffPilot.jobs.listAiCountPage,
    AI_COUNTS_ENABLED && showEditTools && planDocumentId ? { jobId, planDocumentId, pageNumber: activePage } : "skip"));
  const aiPage = AI_COUNTS_ENABLED && !isViewOnly ? aiPageData : undefined;
  useEffect(() => setAiPlacement(false), [activePage, planDocumentId, planRevisionId, documentScopeKey]);
  const [pageCount, setPageCount] = useState(1);
  const [showOverlays, setShowOverlays] = useState(true);
  const [activeToolsetId, setActiveToolsetId] = useState<
    "scale" | "measurement" | "tags" | null
  >(null);
  const [activeTool, setActiveTool] = useState<ToolType | null>(null);
  const [activeMeasurementType, setActiveMeasurementType] = useState<MeasurementType>(
    MEASUREMENT_TYPES.SQFT
  );
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [angleLockEnabled, setAngleLockEnabled] = useState(false);
  const [incrementSnapEnabled, setIncrementSnapEnabled] = useState(false);
  const [incrementStepFeet, setIncrementStepFeet] = useState(DEFAULT_INCREMENT_STEP_FEET);
  const [incrementSnapStorageLoaded, setIncrementSnapStorageLoaded] = useState(false);
  const { available: snapAvailable } = useSnapArtifactsAvailable(planRevisionId);
  const pageLabels = useRevisionSheetLabels(planRevisionId);
  useEffect(() => {
    if (!snapAvailable && snapEnabled) {
      setSnapEnabled(false);
    }
  }, [snapAvailable, snapEnabled]);
  const [heightValue, setHeightValue] = useState("");
  const [draftMeasurement, setDraftMeasurement] = useState<DraftMeasurementState | null>(null);
  const draftContext = useRef({ jobId, planDocumentId, planRevisionId, documentScopeKey, activePage, draft: draftMeasurement, generation: 0 });
  useLayoutEffect(() => {
    const previous = draftContext.current;
    const scopeChanged = previous.jobId !== jobId || previous.planDocumentId !== planDocumentId || previous.planRevisionId !== planRevisionId || previous.documentScopeKey !== documentScopeKey || previous.activePage !== activePage;
    draftContext.current = { jobId, planDocumentId, planRevisionId, documentScopeKey, activePage, draft: draftMeasurement, generation: previous.generation + (scopeChanged ? 1 : 0) };
  }, [jobId, planDocumentId, planRevisionId, documentScopeKey, activePage, draftMeasurement]);
  const [isSavingDraftMeasurement, setIsSavingDraftMeasurement] = useState(false);
  const [isReassigningMeasurementId, setIsReassigningMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const [isDeletingMeasurementId, setIsDeletingMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState<
    Id<"takeoffPilotMeasurements">[]
  >([]);
  const [aiDrawing, setAiDrawing] = useState<AiDrawing | null>(null);
  const tracingAiProposal = aiDrawing?.kind === "trace" ? aiDrawing : null;
  const manualAiMeasurement = aiDrawing?.kind === "new" ? aiDrawing : null;
  useEffect(() => { setAiDrawing(null); }, [activePage, planDocumentId, planRevisionId, documentScopeKey]);
  const [activeMeasurementId, setActiveMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const [selectedTagDetectionIds, setSelectedTagDetectionIds] = useState<string[]>([]);
  const { textLocked, textFontSize, handleTextLockToggle, handleTextFontSizeChange } =
    useTakeoffTextSettings({
      jobId,
      planDocumentId: planDocumentId ?? undefined,
      planRevisionId: planRevisionId ?? undefined,
      persistedTextLocked,
      persistedTextFontSize,
      canEdit,
    });
  const [copiedMeasurementIds, setCopiedMeasurementIds] = useState<
    Id<"takeoffPilotMeasurements">[]
  >([]);
  const [activeTagTool, setActiveTagTool] = useState<TagToolId | null>(null);
  const [selectedAddTagOptionId, setSelectedAddTagOptionId] = useState<string | null>(null);
  const [lastUsedMeasurementTagId, setLastUsedMeasurementTagId] =
    useState<Id<"takeoffPilotExtractedTags"> | null>(null);
  const [activePageAspectRatio, setActivePageAspectRatio] = useState<number | undefined>(undefined);
  const [activePagePixelMetrics, setActivePagePixelMetrics] = useState({
    width: 0,
    height: 0,
  });
  const viewportRef = useRef<DocumentViewportValue | null>(null);
  const pendingDetectionFocusRef = useRef<DetectionRecord | null>(null);
  const measurementScale = useMeasurementScale({
    scopeKey: documentScopeKey,
    projectId: planProjectId,
    documentId: planSourceDocumentId,
    revisionId: planRevisionId,
    pageNumber: activePage,
    pageAspectRatio: activePageAspectRatio,
  });
  // A member demoted mid-session drops out of any tool, draft, or calibration they had open.
  useOnEditAccessLost(() => {
    setActiveToolsetId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setDraftMeasurement(null);
    measurementScale.cancelCalibration();
    setAiPlacement(false);
    setAiDrawing(null);
  });
  const pointerOwner = showEditTools && aiPlacement ? "external" :
    showEditTools &&
    (measurementScale.isCalibrationMode || activeTool || activeTagTool || activeToolsetId === "tags")
      ? "tool"
      : "viewport";
  const currentHeightFeet = useMemo(() => {
    const parsed = parseFeetInches(heightValue);
    return parsed && parsed > 0 ? parsed : undefined;
  }, [heightValue]);
  const handleMeasurementToolChange = useCallback((tool: ToolType | null) => {
    setAiDrawing(null);
    setActiveTool(tool);
    if (tool) {
      setActiveTagTool(null);
    }
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
  }, []);
  const handleMeasurementTypeChange = useCallback((type: MeasurementType) => {
    setAiDrawing(null);
    setActiveMeasurementType(type);
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
  }, []);
  const railTagOptions = useMemo(
    () => tags.map((tag) => ({ id: String(tag.tagId), label: tag.rawValue })),
    [tags]
  );
  const handleTagsToolSelect = useCallback((toolId: TagToolId | null) => {
    setActiveTagTool(toolId);
    if (toolId) {
      setAiDrawing(null);
      setActiveTool(null);
      setDraftMeasurement(null);
      setSelectedMeasurementIds([]);
      setActiveMeasurementId(null);
      setActiveToolsetId("tags");
    }
  }, []);
  const overlaysEnabled = !isViewOnly && showOverlays;
  useEffect(() => {
    if (railTagOptions.length === 0) {
      setSelectedAddTagOptionId(null);
      return;
    }

    if (
      selectedAddTagOptionId &&
      railTagOptions.some((option) => option.id === selectedAddTagOptionId)
    ) {
      return;
    }

    setSelectedAddTagOptionId(railTagOptions[0].id);
  }, [railTagOptions, selectedAddTagOptionId]);

  const { enabledToolsets: railToolsets, controllers: railControllers } =
    useTakeoffViewportToolsetControllers({
      measurementScale,
      activeToolsetId,
      onActiveToolsetChange: setActiveToolsetId,
      activeTool,
      activeMeasurementType,
      onMeasurementToolChange: handleMeasurementToolChange,
      onMeasurementTypeChange: handleMeasurementTypeChange,
      snapEnabled,
      onSnapToggle: () => setSnapEnabled(!snapEnabled),
      snapAvailable,
      angleLockEnabled,
      onAngleLockToggle: () => setAngleLockEnabled((current) => !current),
      incrementSnapEnabled,
      onIncrementSnapToggle: () => setIncrementSnapEnabled((current) => !current),
      incrementStepFeet,
      onIncrementStepChange: setIncrementStepFeet,
      heightValue,
      onHeightChange: setHeightValue,
      textLocked,
      onTextLockToggle: handleTextLockToggle,
      textFontSize,
      onTextFontSizeChange: handleTextFontSizeChange,
      activeTagTool,
      tagOptions: railTagOptions,
      selectedTagOptionId: selectedAddTagOptionId,
      onSelectedTagOptionChange: setSelectedAddTagOptionId,
      showOverlays: overlaysEnabled,
      onTagsToolSelect: handleTagsToolSelect,
      pagePixelWidth: activePagePixelMetrics.width,
      pagePixelHeight: activePagePixelMetrics.height,
    });
  const headerToolsets = useMemo(
    () =>
      getViewportToolsetsForPlacement(
        VIEWPORT_VIEW_IDS.takeoffPilot,
        VIEWPORT_TOOLSET_PLACEMENTS.topToolbar
      ),
    []
  );
  const recordActionLog = useRecordActionLog();
  const createMeasurement = useLoggedMutation(api.takeoffPilot.measurements.create);
  const updateMeasurement = useLoggedMutation(api.takeoffPilot.measurements.update);
  const restoreMeasurementEditState = useLoggedMutation(api.takeoffPilot.measurements.restoreEditState);
  const removeMeasurements = useLoggedMutation(api.takeoffPilot.measurements.removeMany);
  const restoreDeletionState = useLoggedMutation(api.takeoffPilot.measurements.restoreDeletionState);
  const reassignMeasurements = useLoggedMutation(api.takeoffPilot.measurements.reassignTagMany);
  const restoreReassignmentState = useLoggedMutation(api.takeoffPilot.measurements.restoreReassignmentState);
  const restoreMeasurementLeaderState = useLoggedMutation(api.takeoffPilot.measurements.restoreMeasurementLeaderState);
  const duplicateMeasurements = useLoggedMutation(api.takeoffPilot.measurements.duplicateMany);
  const applyMeasurementLeaders = useLoggedMutation(api.takeoffPilot.measurements.applyLeaderMany);
  const reviewAiBatch = useLoggedMutation(api.takeoffPilot.aiReviewActions.review);
  const restoreAiReview = useLoggedMutation(api.takeoffPilot.aiReviewActions.restore);
  const undoLegacyAiReview = useLoggedMutation(api.takeoffPilot.aiReviewActions.undo);
  const localJobHistory = useTakeoffJobHistory(jobId, !jobHistory && showEditTools);
  const { history, aiHistory, measurementHistory, aiReviewCursors, measurementEditQueues: measurementEditQueuesRef, latestMeasurementState: latestMeasurementStateRef, enqueue: enqueueHistoryEdit, pendingCount: pendingMeasurementEditCount } = jobHistory ?? localJobHistory;
  const [aiHistorySession, setAiHistorySession] = useState<string>();
  useEffect(() => { let active = true; void getAiHistorySession().then(id => { if (active) setAiHistorySession(id); }); return () => { active = false; }; }, []);
  const jobScope = useMemo(() => ({}), [jobId]);
  const activeJobRef = useRef<object | null>(jobScope);
  useLayoutEffect(() => {
    activeJobRef.current = jobScope;
    setIsSavingDraftMeasurement(false);
    setIsDeletingMeasurementId(null);
    setIsReassigningMeasurementId(null);
    return () => { if (activeJobRef.current === jobScope) activeJobRef.current = null; };
  }, [jobScope]);
  const isCurrentJob = () => activeJobRef.current === jobScope;
  useLayoutEffect(() => {
    setIsDeletingMeasurementId(null);
    setIsReassigningMeasurementId(null);
  }, [jobScope, planDocumentId, planRevisionId, documentScopeKey, activePage]);
  const { saveDrawing: saveAiDrawing, saveTrace: saveAiTrace } = useAiDrawing(history.record, aiHistory, measurementHistory);

  const handleUndo = useCallback(async () => {
    try {
      await history.undo();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to undo the last change");
    }
  }, [history]);

  const handleRedo = useCallback(async () => {
    try {
      await history.redo();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to redo the last change");
    }
  }, [history]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setIncrementSnapStorageLoaded(true);
      return;
    }

    try {
      const stored = window.localStorage.getItem(MEASUREMENT_INCREMENT_STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as { enabled?: unknown; stepFeet?: unknown };
      if (typeof parsed.enabled === "boolean") {
        setIncrementSnapEnabled(parsed.enabled);
      }
      if (
        typeof parsed.stepFeet === "number" &&
        VALID_MEASUREMENT_INCREMENT_STEPS.has(parsed.stepFeet)
      ) {
        setIncrementStepFeet(parsed.stepFeet);
      }
    } catch {
      // Ignore invalid persisted values and keep defaults.
    } finally {
      setIncrementSnapStorageLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!incrementSnapStorageLoaded || typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      MEASUREMENT_INCREMENT_STORAGE_KEY,
      JSON.stringify({
        enabled: incrementSnapEnabled,
        stepFeet: incrementStepFeet,
      })
    );
  }, [incrementSnapEnabled, incrementSnapStorageLoaded, incrementStepFeet]);

  const normalizedTexts = useMemo(
    () =>
      Array.from(new Set(tags.map((tag) => tag.normalizedValue).filter((value) => value.length > 0))),
    [tags]
  );
  const colorByNormalizedText = useMemo(
    () =>
      new Map(
        tags
          .filter((tag) => tag.colorHex && tag.normalizedValue.length > 0)
          .map((tag) => [tag.normalizedValue, tag.colorHex!] as const)
      ),
    [tags]
  );
  const colorByRawText = useMemo(
    () =>
      new Map(
        tags
          .filter((tag) => tag.colorHex && tag.rawValue.trim().length > 0)
          .map((tag) => [tag.rawValue.trim(), tag.colorHex!] as const)
      ),
    [tags]
  );
  const colorByTagId = useMemo(
    () => new Map(tags.map((tag) => [String(tag.tagId), tag.colorHex ?? DEFAULT_TAG_COLOR])),
    [tags]
  );
  const labelByTagId = useMemo(
    () => new Map(tags.map((tag) => [String(tag.tagId), tag.rawValue])),
    [tags]
  );
  const measurementTags = useMemo<MeasurementTagOption[]>(
    () =>
      tags.map((tag) => ({
        id: tag.tagId,
        label: tag.rawValue,
      })),
    [tags]
  );
  const measurementTagIdSet = useMemo(
    () => new Set(measurementTags.map((tag) => String(tag.id))),
    [measurementTags]
  );
  const measurementTagIds = useMemo(
    () => measurementTags.map((tag) => tag.id),
    [measurementTags]
  );

  const { data: pageDetections, isPending } = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevisionPage,
      getRevisionPageDetectionQueryArgs({
        revisionId: planRevisionId,
        pageNumber: activePage,
        normalizedTexts,
      })
    )
  );
  const { data: allDetections } = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevision,
      getRevisionDetectionQueryArgs({
        revisionId: planRevisionId,
        normalizedTexts,
      })
    )
  );

  const { data: pageMeasurements = [] } = useQuery(
    convexQuery(
      api.takeoffPilot.measurements.listByJobPage,
      planDocumentId && !isViewOnly
        ? {
            jobId,
            planDocumentId,
            pageNumber: activePage,
            tagIds: measurementTagIds,
          }
        : "skip"
    )
  );
  const handleReviewAiBatch = async (args: AiBatchReview) => {
    if (history.isApplying()) throw new UndoRedoBusyError();
    return enqueueHistoryEdit(async () => {
      const lineageSessionId = await getAiHistorySession();
      const cursors = aiHistory.track(args.items.map(item => ({ proposalId: item.proposalId, version: item.expectedVersion })));
      const expected = aiHistory.expected(cursors);
      const result = await reviewAiBatch({ ...args, jobId, lineageSessionId, items: args.items.map((item, index) => ({ ...item, expectedVersion: expected[index].version })) });
      const { actionId } = result;
      aiHistory.advance(cursors, expected.map(item => ({ proposalId: item.proposalId, beforeVersion: item.version, version: item.version + 1 })));
      const measurementCursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(measurementCursors, result.measurementVersions);
      aiReviewCursors.set(actionId, { proposals: cursors, measurements: measurementCursors });
      const restore = async (direction: "undo" | "redo") => {
        const result = await restoreAiReview({ actionId, direction, lineageSessionId, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors) });
        aiHistory.advance(cursors, result.aiVersions);
        measurementHistory.advance(measurementCursors, result.measurementVersions);
      };
      history.record({ undo: () => restore("undo"), redo: () => restore("redo") });
    });
  };
  const handleUndoLatestAiReview = async (action: AiLatestReview) => {
    if (history.isApplying()) throw new UndoRedoBusyError();
    return enqueueHistoryEdit(async () => {
      const lineageSessionId = await getAiHistorySession();
      const existing = aiReviewCursors.get(action.actionId);
      const result = existing || (action.restorable && action.measurementVersions.length === action.items.length * 4)
        ? await restoreAiReview({ actionId: action.actionId, direction: "undo", lineageSessionId, expectedAiVersions: existing ? aiHistory.expected(existing.proposals) : action.items, expectedMeasurementVersions: existing ? measurementHistory.expected(existing.measurements) : action.measurementVersions })
        : await undoLegacyAiReview({ actionId: action.actionId, lineageSessionId });
      const cursors = existing?.proposals ?? aiHistory.track(result.aiVersions.map(item => ({ proposalId: item.proposalId, version: item.beforeVersion })));
      const measurementCursors = existing?.measurements ?? measurementHistory.track(result.measurementVersions);
      aiHistory.advance(cursors, result.aiVersions);
      measurementHistory.advance(measurementCursors, result.measurementVersions);
      aiReviewCursors.set(action.actionId, { proposals: cursors, measurements: measurementCursors });
      const restore = async (direction: "undo" | "redo") => {
        const restored = await restoreAiReview({ actionId: action.actionId, direction, lineageSessionId, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors) });
        aiHistory.advance(cursors, restored.aiVersions);
        measurementHistory.advance(measurementCursors, restored.measurementVersions);
      };
      history.record({ undo: () => restore("redo"), redo: () => restore("undo") });
    });
  };
  const {
    handleGroupLabels,
    handleUngroupLabels,
    handleGroupAllLabelsInScope,
    handleUngroupAllLabelsInScope,
  } = useMeasurementLabelGrouping({
    jobId,
    planDocumentId,
    activePage,
    pageMeasurements: pageMeasurements as MeasurementOverlayRecord[],
    selectedMeasurementIds,
    history,
    enqueueHistoryEdit,
    measurementHistory,
    getMeasurementMutationErrorMessage,
  });
  useEffect(() => {
    for (const measurement of pageMeasurements as MeasurementOverlayRecord[]) {
      const key = String(measurement._id);
      if (!measurementEditQueuesRef.has(key)) {
        latestMeasurementStateRef.set(key, measurement);
      }
    }
  }, [pageMeasurements, measurementEditQueuesRef, latestMeasurementStateRef]);
  const enqueueMeasurementEdit = useCallback(
    <T,>(measurementId: Id<"takeoffPilotMeasurements">, operation: () => Promise<T>) => {
      const key = String(measurementId);
      const result = enqueueHistoryEdit(operation);
      const tail = result.then(() => undefined, () => undefined);
      measurementEditQueuesRef.set(key, tail);
      void tail.finally(() => {
        if (measurementEditQueuesRef.get(key) === tail) {
          measurementEditQueuesRef.delete(key);
        }
      });
      return result;
    },
    [enqueueHistoryEdit, measurementEditQueuesRef]
  );
  const measurementAiVersions = (ids: Id<"takeoffPilotMeasurements">[]) => {
    return [...new Set(ids)].flatMap((id) => {
      const measurement = (pageMeasurements as MeasurementOverlayRecord[]).find((row) => row._id === id);
      const proposal = aiPage?.proposals.find((item) => item.measurementId === id);
      if (measurement?.aiProposalId) {
        if (measurement.aiProposalVersion === undefined) throw new Error("Wait for the latest AI measurement before editing");
        return [{ proposalId: measurement.aiProposalId, version: measurement.aiProposalVersion }];
      }
      if (proposal) return [{ proposalId: proposal._id, version: proposal.version }];
      if (!measurement) throw new Error("Wait for the latest measurements before editing");
      return [];
    });
  };
  const recordCreatedMeasurements = (measurementIds: Id<"takeoffPilotMeasurements">[], versions: AiVersion[]) => {
    const cursors = aiHistory.track(versions);
    let deletion: Awaited<ReturnType<typeof removeMeasurements>>["history"] | undefined;
    let aiActionId: Awaited<ReturnType<typeof removeMeasurements>>["aiActionId"];
    let measurementCursors: ReturnType<MeasurementHistoryLineage["track"]> = [];
    history.record({
      undo: async () => {
        const lineageSessionId = cursors.length ? await getAiHistorySession() : undefined;
        const result = await removeMeasurements({ measurementIds, expectedAiVersions: aiHistory.expected(cursors), ...(lineageSessionId ? { lineageSessionId } : {}) });
        aiHistory.advance(cursors, result.aiVersions);
        measurementCursors = measurementHistory.track(result.measurementVersions);
        measurementHistory.advance(measurementCursors, result.measurementVersions);
        deletion = result.history;
        aiActionId = result.aiActionId;
      },
      redo: async () => {
        if (!deletion) throw new Error("Measurement creation cannot be redone");
        const lineageSessionId = cursors.length ? await getAiHistorySession() : undefined;
        const result = await restoreDeletionState({ jobId, expected: deletion.after, target: deletion.before, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(aiActionId ? { aiActionId, direction: "undo" as const } : {}) });
        aiHistory.advance(cursors, result.aiVersions);
        measurementHistory.advance(measurementCursors, result.measurementVersions);
      },
    });
  };
  // Source tag boxes remain useful for reviewing AI assignments and missing items.
  const activePageDetections = (pageDetections?.activeDetections ?? []) as DetectionRecord[];
  const archivedPageDetections = (pageDetections?.archivedDetections ?? []) as DetectionRecord[];
  const allActiveDetections = (allDetections?.activeDetections ?? []) as DetectionRecord[];
  const tagPlacementOptions = useMemo<DocumentTagOption[]>(() => {
    const detections = [...activePageDetections, ...allActiveDetections];
    return tags.map((tag) => {
      const matchingDetection = detections.find(
        (detection) =>
          detection.normalizedText === tag.normalizedValue ||
          detection.rawText.trim() === tag.rawValue.trim()
      );

      return {
        id: String(tag.tagId),
        label: tag.rawValue,
        rawText: tag.rawValue,
        normalizedText: tag.normalizedValue,
        color: tag.colorHex ?? colorByTagId.get(String(tag.tagId)),
        ...(matchingDetection
          ? {
              classification: matchingDetection.classification,
              bbox: matchingDetection.bbox,
            }
          : {}),
      };
    });
  }, [activePageDetections, allActiveDetections, colorByTagId, tags]);
  const tagPlacementMode = getTagPlacementMode(activeTagTool);
  const getDetectionColor = (detection: DetectionRecord) =>
    colorByRawText.get(detection.rawText.trim()) ??
    colorByNormalizedText.get(detection.normalizedText) ??
    DEFAULT_TAG_COLOR;

  const {
    focusPagePoint,
    focusMeasurementGeometry,
    focusMeasurementBreakdownRow,
    pendingMeasurementFocusRef,
  } = useTakeoffViewerFocus({
    viewportRef,
    activePage,
    planDocumentId,
    pageMeasurements: pageMeasurements as MeasurementOverlayRecord[],
    onSelectPlanDocumentId,
    sharedPendingFocusRef,
    setSelectedTagDetectionIds,
    setSelectedMeasurementIds,
    setActiveMeasurementId,
  });
  const focusDetection = useCallback((detection: DetectionRecord) => focusPagePoint({
    x: detection.bbox.x + detection.bbox.width / 2,
    y: detection.bbox.y + detection.bbox.height / 2,
  }), [focusPagePoint]);
  const handleViewportChange = useCallback((viewport: DocumentViewportValue) => {
    viewportRef.current = viewport;
    const nextAspectRatio = viewport.activePageMetrics?.aspectRatio;
    const nextWidth = viewport.activePageMetrics?.width ?? 0;
    const nextHeight = viewport.activePageMetrics?.height ?? 0;
    const normalizedAspectRatio =
      Number.isFinite(nextAspectRatio) && (nextAspectRatio as number) > 0
        ? (nextAspectRatio as number)
        : undefined;

    setActivePagePixelMetrics((current) =>
      current.width === nextWidth && current.height === nextHeight
        ? current
        : {
            width: nextWidth,
            height: nextHeight,
          }
    );
    setActivePageAspectRatio((current) =>
      current === normalizedAspectRatio ? current : normalizedAspectRatio
    );
  }, []);

  const {
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    highlightedDetectionId,
    navigableTagIds,
  } = useTagInstanceNavigation({
    tags,
    activeDetections: allActiveDetections,
    enabled: overlaysEnabled,
    navigationScopeKey: `${String(planDocumentId ?? "none")}:${String(planRevisionId)}`,
    onFocusDetection: (detection) => {
      if (detection.pageNumber !== activePage) {
        pendingDetectionFocusRef.current = detection;
        viewportRef.current?.goToPage(detection.pageNumber);
        return;
      }

      pendingDetectionFocusRef.current = null;
      focusDetection(detection);
    },
  });

  const { focusRequest: aiFocusRequest, setFocusRequest: setAiFocusRequest, navigate: navigateAiMark } = useAiReviewNavigation({
    page: aiPage,
    activePage,
    viewportRef,
    focusPagePoint,
  });
  useImperativeHandle(ref, () => ({
    focusNextInstanceForTag:(tagId)=>{if(!navigateAiMark(String(tagId),1))focusNextInstanceForTag(tagId);},
    focusPreviousInstanceForTag:(tagId)=>{if(!navigateAiMark(String(tagId),-1))focusPreviousInstanceForTag(tagId);},
    focusMeasurementBreakdownRow,
  }), [focusMeasurementBreakdownRow, focusNextInstanceForTag, focusPreviousInstanceForTag,navigateAiMark]);

  useEffect(() => {
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setCopiedMeasurementIds([]);
    setActivePageAspectRatio(undefined);
    setActiveToolsetId(null);
  }, [activePage]);

  const previousFocusPlanRef = useRef<Id<"takeoffPilotPlanDocuments"> | null | undefined>(undefined);
  useEffect(() => {
    if (previousFocusPlanRef.current !== planDocumentId) {
      const target = pendingMeasurementFocusRef.current;
      if (target && target.planDocumentId !== planDocumentId) pendingMeasurementFocusRef.current = null;
      previousFocusPlanRef.current = planDocumentId;
    }
    setActivePage(1);
    setDraftMeasurement(null);
    setSelectedTagDetectionIds([]);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setCopiedMeasurementIds([]);
    setActivePageAspectRatio(undefined);
    setActiveToolsetId(null);
    pendingDetectionFocusRef.current = null;
  }, [planDocumentId, planRevisionId, documentScopeKey]);

  useEffect(() => {
    if (!aiReviewLocation || aiReviewLocation.planDocumentId !== planDocumentId || aiReviewLocation.pageNumber > pageCount) return;
    viewportRef.current?.goToPage(aiReviewLocation.pageNumber);
    setActivePage(aiReviewLocation.pageNumber);
    if (aiReviewLocation.firstProposalId) setAiFocusRequest({ id: aiReviewLocation.firstProposalId, sequence: aiReviewLocation.sequence });
  }, [aiReviewLocation, planDocumentId, pageCount]);

  useEffect(() => {
    const pendingDetection = pendingDetectionFocusRef.current;
    if (!pendingDetection || pendingDetection.pageNumber !== activePage) {
      return;
    }

    focusDetection(pendingDetection);
    pendingDetectionFocusRef.current = null;
  }, [activePage, focusDetection]);

  useEffect(() => {
    onNavigableTagIdsChange?.(Array.from(new Set([...navigableTagIds,...(aiPage?.navigationProposals.filter(mark=>tags.some(tag=>tag.tagId===mark.tagId)).map(mark=>String(mark.tagId))??[])])));
  }, [navigableTagIds, onNavigableTagIdsChange,aiPage,tags]);

  useEffect(() => {
    const pendingTarget = pendingMeasurementFocusRef.current;
    if (!pendingTarget) {
      return;
    }

    if (String(planDocumentId ?? "") !== String(pendingTarget.planDocumentId)) {
      return;
    }

    if (activePage !== pendingTarget.pageNumber) {
      viewportRef.current?.goToPage(pendingTarget.pageNumber);
      return;
    }

    const measurement = (pageMeasurements as MeasurementOverlayRecord[]).find(
      (row) => String(row._id) === String(pendingTarget.measurementId)
    );
    if (!measurement) {
      return;
    }

    setSelectedTagDetectionIds([]);
    setSelectedMeasurementIds([measurement._id]);
    setActiveMeasurementId(measurement._id);
    focusMeasurementGeometry(measurement.geometry);
    pendingMeasurementFocusRef.current = null;
  }, [activePage, focusMeasurementGeometry, pageMeasurements, planDocumentId]);

  useEffect(() => {
    if (lastUsedMeasurementTagId && !measurementTagIdSet.has(String(lastUsedMeasurementTagId))) {
      setLastUsedMeasurementTagId(null);
    }
  }, [lastUsedMeasurementTagId, measurementTagIdSet]);

  useEffect(() => {
    setDraftMeasurement((current) => {
      if (!current?.tagId) {
        return current;
      }
      return measurementTagIdSet.has(String(current.tagId))
        ? current
        : { ...current, tagId: undefined };
    });
  }, [measurementTagIdSet]);

  useEffect(() => {
    if (overlaysEnabled) {
      return;
    }
    setSelectedTagDetectionIds([]);
    setActiveTagTool(null);
    setActiveToolsetId((current) => (current === "tags" ? null : current));
  }, [overlaysEnabled]);

  useEffect(() => {
    const activeScale = measurementScale.scale;
    if (!activeScale) {
      return;
    }
    setDraftMeasurement((current) => {
      if (
        !current ||
        !(
          current.measurementType === MEASUREMENT_TYPES.CF ||
          ((current.tool === TOOL_IDS.line || current.tool === TOOL_IDS.polyline) &&
            current.measurementType === MEASUREMENT_TYPES.SQFT)
        )
      ) {
        return current;
      }
      if (current.height === currentHeightFeet) {
        return current;
      }
      const nextValue = currentHeightFeet
        ? computeMeasurementValue(
            current.tool,
            current.measurementType,
            current.geometry,
            currentHeightFeet,
            activeScale.pixelsPerFoot,
            activeScale.pageAspectRatio
          )
        : 0;
      return {
        ...current,
        height: currentHeightFeet,
        value: nextValue,
      };
    });
  }, [currentHeightFeet, measurementScale.scale]);

  const source = useMemo<DocumentSource | null>(() => {
    if (!planFileUrl) {
      return null;
    }

    return {
      revisionId: String(planRevisionId),
      fileUrl: planFileUrl,
      filename: planFilename,
      mediaKind: getDocumentMediaKind(planFilename, "application/pdf"),
      mimeType: "application/pdf",
    };
  }, [planFileUrl, planFilename, planRevisionId]);

  if (!source) {
    return (
      <div className="flex h-[720px] items-center justify-center rounded-xl border bg-muted/20 text-sm text-muted-foreground">
        Unable to load the selected plan document.
      </div>
    );
  }

  const handleDraftMeasurementChange = (draft: DraftMeasurementState | null) => {
    setDraftMeasurement(
      draft
        ? {
            ...draft,
            tagId:
              draft.tagId ??
              (lastUsedMeasurementTagId && measurementTagIdSet.has(String(lastUsedMeasurementTagId))
                ? lastUsedMeasurementTagId
                : undefined),
          }
        : null
    );
  };

  const beginAiDrawing = (tagId: Id<"takeoffPilotExtractedTags">, basis: "LF" | "SQFT") => {
    if (!measurementScale.scale) {
      toast.error("Confirm the page scale before manually tracing this measurement");
      return false;
    }
    setLastUsedMeasurementTagId(tagId);
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
    setSelectedTagDetectionIds([]);
    setActiveMeasurementType(basis);
    setActiveTool(basis === "SQFT" ? TOOL_IDS.polygon : TOOL_IDS.polyline);
    setActiveToolsetId("measurement");
    return true;
  };
  const beginAiTrace = (mark: AiCountPage["proposals"][number], mode: "replace" | "cutout") => {
    const run = aiPage?.runs.find(run => run._id === mark.runId);
    const basis = run?.measurementBasis === "SQFT" ? "SQFT" : "LF";
    if (!beginAiDrawing(mark.tagId, basis)) return;
    setAiDrawing({ kind: "trace", proposalId: mark._id, version: mark.version, tagId: mark.tagId,
      previousGeometryJson: mark.geometryJson ?? JSON.stringify({ kind: "point", points: [[mark.x, mark.y]] }), basis, mode });
  };
  const handleSaveDraftMeasurement = async () => {
    const draft = draftMeasurement;
    if (!draft || isHistoryMutationBlocked(history.isApplying(), isSavingDraftMeasurement)) return;
    if (!planDocumentId) {
      toast.error("Measurements are unavailable for this document");
      return;
    }
    if (!draft.tagId) {
      toast.error("Choose a tag for the measurement");
      return;
    }
    const generation = draftContext.current.generation;
    setIsSavingDraftMeasurement(true);
    return enqueueHistoryEdit(async () => { try {
      const createArgs = {
        jobId,
        planDocumentId,
        pageNumber: activePage,
        tagId: draft.tagId,
        tool: draft.tool,
        measurementType: draft.measurementType,
        geometry: draft.geometry,
        annotation: draft.annotation,
        height: draft.height,
        value: draft.value,
      };
      const tracing = aiDrawing?.kind === "trace";
      const result = aiDrawing
        ? await saveAiDrawing(aiDrawing, draft)
        : { measurementId: await createMeasurement(createArgs), created: true, aiVersions: [] };
      if (!tracing && result.created) recordCreatedMeasurements([result.measurementId], result.aiVersions);
      if (!isCurrentJob()) return;
      if (draftContext.current.generation === generation && draftContext.current.draft === draft) {
        setAiDrawing(null);
        setLastUsedMeasurementTagId(draft.tagId ?? null);
        setDraftMeasurement(null);
        setSelectedMeasurementIds([]);
        setActiveMeasurementId(null);
      }
      toast.success("Measurement saved");
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to save measurement"));
      throw error;
    } finally {
      if (isCurrentJob()) setIsSavingDraftMeasurement(false);
    } });
  };

  const handleUpdateMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: DraftMeasurementState["geometry"],
    height: number | undefined,
    value: number
  ) => {
    if (history.isApplying()) throw new UndoRedoBusyError();
    return enqueueMeasurementEdit(measurementId, async () => {
      const key = String(measurementId);
      const current = latestMeasurementStateRef.get(key) ??
        (pageMeasurements as MeasurementOverlayRecord[]).find((measurement) => String(measurement._id) === key);
      if (!current) return;
      if (current.aiProposalId && current.aiProposalVersion === undefined) throw new Error("Wait for the latest AI measurement before editing");
      const cursors = aiHistory.track(current.aiProposalId && current.aiProposalVersion !== undefined
        ? [{ proposalId: current.aiProposalId, version: current.aiProposalVersion }] : []);
      const lineageSessionId = cursors.length ? await getAiHistorySession() : undefined;
      const next = { ...current, geometry, height, value };
      const result = await updateMeasurement({ measurementId, geometry, height, clearHeight: height === undefined, value, expectedAiVersions: aiHistory.expected(cursors), ...(lineageSessionId ? { lineageSessionId } : {}) });
      aiHistory.advance(cursors, result.aiVersions);
      const measurementCursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(measurementCursors, result.measurementVersions);
      latestMeasurementStateRef.set(key, next);
      const change = result.history;
      if (change?.before.kind === "geometry" && change.after.kind === "geometry") history.record({
        undo: async () => {
          const restored = await restoreMeasurementEditState({ expected: change.after, target: change.before, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "undo" as const } : {}) });
          aiHistory.advance(cursors, restored.aiVersions);
          measurementHistory.advance(measurementCursors, restored.measurementVersions);
          if (change.before.kind === "geometry") latestMeasurementStateRef.set(key, { ...current, geometry: change.before.geometry, height: change.before.height ?? undefined, value: change.before.value });
        },
        redo: async () => {
          const restored = await restoreMeasurementEditState({ expected: change.before, target: change.after, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "redo" as const } : {}) });
          aiHistory.advance(cursors, restored.aiVersions);
          measurementHistory.advance(measurementCursors, restored.measurementVersions);
          if (change.after.kind === "geometry") latestMeasurementStateRef.set(key, { ...next, geometry: change.after.geometry, height: change.after.height ?? undefined, value: change.after.value });
        },
      });
    });
  };

  const handleUpdateMeasurementAnnotation = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => {
    if (history.isApplying()) throw new UndoRedoBusyError();
    return enqueueMeasurementEdit(measurementId, async () => {
      const clearAnnotationFields = (["textOffset", "leaderVisible", "strokeWidth", "textBoxWidth"] as const)
        .filter((field) => Object.hasOwn(annotation, field) && annotation[field] === undefined);
      const values = { ...annotation };
      for (const field of clearAnnotationFields) delete values[field];
      const result = await updateMeasurement({
        measurementId,
        annotation: values,
        ...(clearAnnotationFields.length > 0 ? { clearAnnotationFields } : {}),
      });
      const change = result.history;
      const cursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(cursors, result.measurementVersions);
      if (change) history.record({
        undo: async () => { const restored = await restoreMeasurementEditState({ expected: change.after, target: change.before, expectedMeasurementVersions: measurementHistory.expected(cursors) }); measurementHistory.advance(cursors, restored.measurementVersions); },
        redo: async () => { const restored = await restoreMeasurementEditState({ expected: change.before, target: change.after, expectedMeasurementVersions: measurementHistory.expected(cursors) }); measurementHistory.advance(cursors, restored.measurementVersions); },
      });
    });
  };

  const handleReassignMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => {
    return handleReassignMeasurements([measurementId], tagId);
  };

  const handleDeleteMeasurement = async (measurementId: Id<"takeoffPilotMeasurements">) => {
    await handleDeleteMeasurements([measurementId]);
  };

  const handleSelectMeasurement = (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => {
    if (!measurementId) {
      setSelectedMeasurementIds([]);
      setActiveMeasurementId(null);
      return;
    }
    setSelectedTagDetectionIds([]);
    if (!options?.shiftKey) {
      setSelectedMeasurementIds([measurementId]);
      setActiveMeasurementId(measurementId);
      return;
    }
    setSelectedMeasurementIds((current) => {
      if (current.includes(measurementId)) {
        const next = current.filter((id) => id !== measurementId);
        setActiveMeasurementId((active) => (active === measurementId ? (next[0] ?? null) : active));
        return next;
      }
      setActiveMeasurementId(measurementId);
      return [...current, measurementId];
    });
  };

  const handleSelectMeasurements = (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    options?: { additive?: boolean }
  ) => {
    const uniqueIds = Array.from(new Set(measurementIds));
    if (uniqueIds.length === 0) {
      return;
    }
    setSelectedTagDetectionIds([]);
    if (!options?.additive) {
      setSelectedMeasurementIds(uniqueIds);
      setActiveMeasurementId(uniqueIds[uniqueIds.length - 1] ?? null);
      return;
    }
    setActiveMeasurementId(uniqueIds[uniqueIds.length - 1] ?? null);
    setSelectedMeasurementIds((current) => {
      const next = Array.from(new Set([...current, ...uniqueIds]));
      return next;
    });
  };

  const handleCopyMeasurement = (measurementId: Id<"takeoffPilotMeasurements">) => {
    const measurement =
      (pageMeasurements as MeasurementOverlayRecord[]).find(
        (item) => item._id === measurementId
      ) ?? null;
    if (!measurement) {
      return;
    }
    setCopiedMeasurementIds([measurement._id]);
  };

  const handleCopyMeasurements = (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    const availableMeasurementIds = new Set(
      (pageMeasurements as MeasurementOverlayRecord[]).map((measurement) =>
        String(measurement._id)
      )
    );
    const nextCopiedIds = measurementIds.filter((measurementId) =>
      availableMeasurementIds.has(String(measurementId))
    );
    if (nextCopiedIds.length > 0) {
      setCopiedMeasurementIds(nextCopiedIds);
    }
  };

  const handlePasteMeasurement = async (targetPoint?: Point) => {
    if (isHistoryMutationBlocked(history.isApplying(), copiedMeasurementIds.length === 0)) {
      return;
    }

    const generation = draftContext.current.generation;
    const isCurrentPage = () => isCurrentJob() && draftContext.current.generation === generation;
    return enqueueHistoryEdit(async () => { try {
      const availableMeasurementIds = new Set(
        (pageMeasurements as MeasurementOverlayRecord[]).map((measurement) =>
          String(measurement._id)
        )
      );
      const validCopiedMeasurementIds = copiedMeasurementIds.filter((measurementId) =>
        availableMeasurementIds.has(String(measurementId))
      );
      if (isCurrentPage() && validCopiedMeasurementIds.length !== copiedMeasurementIds.length) {
        setCopiedMeasurementIds(validCopiedMeasurementIds);
      }
      if (validCopiedMeasurementIds.length === 0) {
        toast.error("No copied measurements are available to paste");
        return;
      }

      const result = await duplicateMeasurements({
        measurementIds: validCopiedMeasurementIds,
        targetPoint,
        expectedAiVersions: aiHistory.expected(aiHistory.track(measurementAiVersions(validCopiedMeasurementIds))),
      });
      const createdIds = result?.createdMeasurementIds ?? [];
      if (createdIds.length > 0) {
        recordCreatedMeasurements(createdIds, result.aiVersions);
      }
      if (!isCurrentPage()) return;
      if (createdIds.length === 0) {
        toast.error("No copied measurements are available to paste");
        return;
      }
      setSelectedTagDetectionIds([]);
      setSelectedMeasurementIds(createdIds);
      setActiveMeasurementId(createdIds[0] ?? null);
      toast.success(
        createdIds.length === 1
          ? "Measurement duplicated"
          : `${createdIds.length} measurements duplicated`
      );
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to duplicate measurement"));
    } });
  };

  const handleDuplicateMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    targetPoint?: Point
  ) => {
    await handleDuplicateMeasurements([measurementId], targetPoint);
  };

  const handleDuplicateMeasurements = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    targetPoint?: Point
  ) => {
    if (isHistoryMutationBlocked(history.isApplying(), measurementIds.length === 0)) {
      return;
    }
    const generation = draftContext.current.generation;
    const isCurrentPage = () => isCurrentJob() && draftContext.current.generation === generation;
    return enqueueHistoryEdit(async () => { try {
      const sourceMeasurements = (pageMeasurements as MeasurementOverlayRecord[]).filter((item) =>
        measurementIds.includes(item._id)
      );
      const result = await duplicateMeasurements({ measurementIds, targetPoint, expectedAiVersions: aiHistory.expected(aiHistory.track(measurementAiVersions(measurementIds))) });
      const createdIds = result?.createdMeasurementIds ?? [];
      if (createdIds.length > 0) {
        recordCreatedMeasurements(createdIds, result.aiVersions);
      }
      if (!isCurrentPage()) return;
      if (createdIds.length > 0) {
        setSelectedTagDetectionIds([]);
      }
      setSelectedMeasurementIds(createdIds);
      setActiveMeasurementId(createdIds[0] ?? null);
      if (sourceMeasurements.length > 0) {
        setCopiedMeasurementIds(sourceMeasurements.map((measurement) => measurement._id));
      }
      toast.success(
        measurementIds.length === 1 ? "Measurement duplicated" : `${measurementIds.length} measurements duplicated`
      );
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to duplicate measurement"));
    } });
  };

  const handleDeleteMeasurements = async (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    if (isHistoryMutationBlocked(history.isApplying(), measurementIds.length === 0)) {
      return;
    }
    const generation = draftContext.current.generation;
    const isCurrentPage = () => isCurrentJob() && draftContext.current.generation === generation;
    return enqueueHistoryEdit(async () => { try {
      if (isCurrentPage() && measurementIds.length === 1) {
        setIsDeletingMeasurementId(measurementIds[0]);
      }
      const cursors = aiHistory.track(measurementAiVersions(measurementIds));
      const lineageSessionId = cursors.length ? await getAiHistorySession() : undefined;
      const result = await removeMeasurements({ measurementIds, expectedAiVersions: aiHistory.expected(cursors), ...(lineageSessionId ? { lineageSessionId } : {}) });
      aiHistory.advance(cursors, result.aiVersions);
      const measurementCursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(measurementCursors, result.measurementVersions);
      const deletedCount = result?.deletedCount ?? 0;
      if (deletedCount > 0) {
        history.record({
          undo: async () => {
            const restored = await restoreDeletionState({ jobId, expected: result.history.after, target: result.history.before, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "undo" as const } : {}) });
            aiHistory.advance(cursors, restored.aiVersions);
            measurementHistory.advance(measurementCursors, restored.measurementVersions);
          },
          redo: async () => {
            const restored = await restoreDeletionState({ jobId, expected: result.history.before, target: result.history.after, expectedAiVersions: aiHistory.expected(cursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "redo" as const } : {}) });
            aiHistory.advance(cursors, restored.aiVersions);
            measurementHistory.advance(measurementCursors, restored.measurementVersions);
          },
        });
        if (!isCurrentPage()) return;
        const deletedSet = new Set(measurementIds.map((id) => String(id)));
        setCopiedMeasurementIds((current) =>
          current.filter((id) => !deletedSet.has(String(id)))
        );
        setSelectedMeasurementIds((current) =>
          current.filter((id) => !deletedSet.has(String(id)))
        );
        setActiveMeasurementId((current) =>
          current && deletedSet.has(String(current)) ? null : current
        );
      }
      toast.success(deletedCount === 1 ? "Measurement deleted" : `${deletedCount} measurements deleted`);
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to delete measurements");
    } finally {
      if (isCurrentPage()) setIsDeletingMeasurementId(null);
    } });
  };

  const handleReassignMeasurements = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => {
    if (isHistoryMutationBlocked(history.isApplying(), measurementIds.length === 0)) {
      return false;
    }
    const generation = draftContext.current.generation;
    const isCurrentPage = () => isCurrentJob() && draftContext.current.generation === generation;
    return enqueueHistoryEdit(async () => { try {
      if (isCurrentPage() && measurementIds.length === 1) {
        setIsReassigningMeasurementId(measurementIds[0]);
      }
      const cursors = aiHistory.track(measurementAiVersions(measurementIds));
      const lineageSessionId = cursors.length ? await getAiHistorySession() : undefined;
      const result = await reassignMeasurements({ measurementIds, tagId, expectedAiVersions: aiHistory.expected(cursors), ...(lineageSessionId ? { lineageSessionId } : {}) });
      aiHistory.advance(cursors, result.aiVersions);
      const measurementCursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(measurementCursors, result.measurementVersions);
      const changedCursors = cursors.filter((cursor) => result.aiVersions.some((item) => item.proposalId === cursor.proposalId));
      const updatedCount = result.updatedCount;
      if (updatedCount === 0) return true;
      if (updatedCount > 0) {
        if (result.history.before.length > 0) {
          history.record({
            undo: async () => {
              const restored = await restoreReassignmentState({ jobId, expected: result.history.after, target: result.history.before, expectedAiVersions: aiHistory.expected(changedCursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "undo" as const } : {}) });
              aiHistory.advance(changedCursors, restored.aiVersions);
              measurementHistory.advance(measurementCursors, restored.measurementVersions);
            },
            redo: async () => {
              const restored = await restoreReassignmentState({ jobId, expected: result.history.before, target: result.history.after, expectedAiVersions: aiHistory.expected(changedCursors), expectedMeasurementVersions: measurementHistory.expected(measurementCursors), ...(lineageSessionId ? { lineageSessionId } : {}), ...(result.aiActionId ? { aiActionId: result.aiActionId, direction: "redo" as const } : {}) });
              aiHistory.advance(changedCursors, restored.aiVersions);
              measurementHistory.advance(measurementCursors, restored.measurementVersions);
            },
          });
        }
      }
      if (!isCurrentPage()) return true;
      setLastUsedMeasurementTagId(tagId);
      toast.success(updatedCount === 1 ? "Measurement updated" : `${updatedCount} measurements updated`);
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to update tag"));
      return false;
    } finally {
      if (isCurrentPage()) setIsReassigningMeasurementId(null);
    } });
  };

  const handleApplyMeasurementLeaders = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => {
    if (isHistoryMutationBlocked(history.isApplying(), measurementIds.length === 0)) {
      return false;
    }
    return enqueueHistoryEdit(async () => { try {
      const result = await applyMeasurementLeaders({ measurementIds, leaderVisible });
      const cursors = measurementHistory.track(result.measurementVersions);
      measurementHistory.advance(cursors, result.measurementVersions);
      if (result.history.before.length > 0) {
        history.record({
          undo: async () => {
            const restored = await restoreMeasurementLeaderState({ jobId, expected: result.history.after, target: result.history.before, expectedMeasurementVersions: measurementHistory.expected(cursors) });
            measurementHistory.advance(cursors, restored.measurementVersions);
          },
          redo: async () => {
            const restored = await restoreMeasurementLeaderState({ jobId, expected: result.history.before, target: result.history.after, expectedMeasurementVersions: measurementHistory.expected(cursors) });
            measurementHistory.advance(cursors, restored.measurementVersions);

          },
        });
      }
      return true;
    } catch (error) {
      console.error(error);
      notifyError(error, getMeasurementMutationErrorMessage(error, "Failed to update measurement leader"));
      return false;
    } });
  };


  return (
    <div className="relative h-full overflow-hidden bg-background">
      <TakeoffViewerHeader
        title={jobName}
        subtitle={jobSubtitle}
        readOnly={!canEdit}
        viewerSources={viewerSources}
        selectedPlanDocumentId={selectedPlanDocumentId}
        onSelectPlanDocumentId={onSelectPlanDocumentId}
        onBack={onClose}
        onExport={onExport}
        isExportInProgress={isExportInProgress}
        tagsCount={tags.length}
        activePage={activePage}
        pageCount={pageCount}
        onGoToPage={(page) => viewportRef.current?.goToPage(page)}
        revisionId={planRevisionId}
        pageLabels={pageLabels}
        toolsets={
          <>
            <CountedTagsToggle
              showOverlays={overlaysEnabled}
              overlaysAvailable={!isViewOnly}
              setShowOverlays={setShowOverlays}
            />
            {AI_COUNTS_ENABLED && showEditTools && planDocumentId && onToggleAiReview && (
              <Toggle ref={aiReviewToggleRef} size="sm" variant="outline" className="px-2.5" pressed={aiReviewOpen} onPressedChange={onToggleAiReview}><Sparkles aria-hidden="true" />AI Review</Toggle>
            )}
            {showEditTools ? (
              <>
                <div aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border" />
                <UndoRedoControls
                  canUndo={history.canUndo}
                  canRedo={history.canRedo}
                  isBusy={isHistoryControlBusy(history.isBusy, pendingMeasurementEditCount)}
                  onUndo={() => void handleUndo()}
                  onRedo={() => void handleRedo()}
                />
                <ViewportToolsetsHeader
                  enabledToolsets={headerToolsets}
                  controllers={railControllers}
                />
              </>
            ) : null}
          </>
        }
      />

      <DocumentViewportHost
        key={documentScopeKey ?? `${planDocumentId}:${planRevisionId}`}
        source={source}
        activePage={activePage}
        onActivePageChange={setActivePage}
        onPageCountChange={setPageCount}
        pageLabels={pageLabels}
        pointerOwner={pointerOwner}
        enableWheelZoom={!measurementScale.isCalibrationMode}
        viewportPadding={{
          top: 80,
          right: 24,
          bottom: 28,
          left: 88,
        }}
        panBoundaryPadding={{ bottom: getBottomPanelPanPadding(bottomPanelHeight) }}
        // pt-12 clears the h-12 ViewerTopBar overlay.
        className="absolute inset-0 overflow-hidden pt-12"
        leftRail={showEditTools ? (
          <ViewportToolsetsRail
            enabledToolsets={railToolsets}
            controllers={railControllers}
          />
        ) : null}
      >
        <ViewportValueBinder onViewportChange={handleViewportChange} />
        <CalibrationOverlay
          calibrationState={measurementScale.calibrationState}
          isCalibrationMode={measurementScale.isCalibrationMode}
          onViewportClick={measurementScale.handleViewportClick}
          onUpdatePoint1={measurementScale.updatePoint1}
          onUpdatePoint2={measurementScale.updatePoint2}
        />
        {(tracingAiProposal || manualAiMeasurement) && (
          <div
            className="absolute left-4 top-4 z-30 rounded border bg-background p-3 text-sm"
            role="status"
            data-viewport-tools-ui
          >
            {tracingAiProposal?.mode === "cutout" ? "Trace the excluded area inside this boundary, then save."
              : (tracingAiProposal ?? manualAiMeasurement)?.basis === "SQFT" ? "Trace the complete finish boundary, then save."
                : "Trace this item's complete path and endpoints, then save."}{" "}
            <button
              type="button"
              className="underline"
              onClick={() => {
                setAiDrawing(null);
                setDraftMeasurement(null);
                setActiveTool(null);
              }}
            >
              Cancel trace
            </button>
          </div>
        )}
        {AI_COUNTS_ENABLED && showEditTools && planDocumentId && (
          <AiCountOverlay
            controlsContainer={aiReviewContainer}
            nativeSelection={selectedMeasurementIds}
            preferredReviewTagId={aiReviewLocation?.tagId}
            onNavigateReview={onNavigateAiReview}
            onSelectNative={ids => { setSelectedMeasurementIds(ids); setActiveMeasurementId(ids[0] ?? null); setActiveTool(null); }}
            onReviewBatch={handleReviewAiBatch}
            onUndoLatestReview={handleUndoLatestAiReview}
            lineageSessionId={aiHistorySession}
            key={`${planDocumentId}:${activePage}`}
            jobId={jobId}
            planDocumentId={planDocumentId}
            pageNumber={activePage}
            tags={tags}
            data={aiPage}
            showMarks={showOverlays}
            interactiveMarks={!activeTool && activeToolsetId !== "tags" && !measurementScale.isCalibrationMode}
            onPlacementChange={setAiPlacement}
            focusRequest={aiFocusRequest}
            onFocusMeasurement={(id) => {
              setSelectedMeasurementIds([id]);
              setActiveMeasurementId(id);
              setActiveTool(null);
              const measurement = (pageMeasurements as MeasurementOverlayRecord[]).find(row => row._id === id);
              if (measurement) focusMeasurementGeometry(measurement.geometry);
            }}
            onTraceMeasurement={mark => beginAiTrace(mark, "replace")}
            onDrawCutout={mark => beginAiTrace(mark, "cutout")}
            onRemoveCutout={(mark, index) => {
              const geometry = JSON.parse(mark.geometryJson!) as AiGeometry;
              if (history.isApplying()) return;
              void enqueueHistoryEdit(() => saveAiTrace({ proposalId: mark._id, version: mark.version, previousGeometryJson: mark.geometryJson! },
                { ...geometry, holes: geometry.holes?.filter((_, current) => current !== index) }))
                .catch(error => notifyError(error, "Unable to remove this cutout"));
            }}
            onAddMeasurement={(runId, tagId, basis) => {
              if (!beginAiDrawing(tagId, basis)) return;
              setAiDrawing({ kind: "new", runId, tagId, basis, requestKey: crypto.randomUUID() });
            }}
          />
        )}
        <MeasurementOverlay
          scale={measurementScale.scale}
          activeTool={activeTool}
          activeMeasurementType={activeMeasurementType}
          currentHeightFeet={currentHeightFeet}
          snapEnabled={snapEnabled}
          angleLockEnabled={angleLockEnabled}
          incrementSnapEnabled={incrementSnapEnabled}
          incrementStepFeet={incrementStepFeet}
          textLocked={textLocked}
          textFontSize={textFontSize}
          measurements={isViewOnly ? [] : (pageMeasurements as MeasurementOverlayRecord[])}
          colorByTagId={colorByTagId}
          labelByTagId={labelByTagId}
          draftMeasurement={draftMeasurement}
          draftTagId={draftMeasurement?.tagId ?? null}
          tags={measurementTags}
          onDraftMeasurementChange={handleDraftMeasurementChange}
          onDraftTagChange={(tagId: Id<"takeoffPilotExtractedTags">) =>
            setDraftMeasurement((current: DraftMeasurementState | null) =>
              current ? { ...current, tagId } : current,
            )
          }
          onCreateCustomLineItem={onCreateCustomLineItem}
          onSaveDraftMeasurement={handleSaveDraftMeasurement}
          isSavingDraftMeasurement={isSavingDraftMeasurement}
          onCancelDraftMeasurement={() => setDraftMeasurement(null)}
          selectedMeasurementIds={selectedMeasurementIds}
          activeMeasurementId={activeMeasurementId}
          onSelectMeasurement={handleSelectMeasurement}
          onSelectMeasurements={handleSelectMeasurements}
          onUpdateMeasurement={handleUpdateMeasurement}
          onUpdateMeasurementAnnotation={handleUpdateMeasurementAnnotation}
          onReassignMeasurement={handleReassignMeasurement}
          onReassignMeasurements={handleReassignMeasurements}
          isReassigningMeasurementId={isReassigningMeasurementId}
          isDeletingMeasurementId={isDeletingMeasurementId}
          onDeleteMeasurement={handleDeleteMeasurement}
          onDeleteMeasurements={handleDeleteMeasurements}
          onApplyMeasurementLeaders={handleApplyMeasurementLeaders}
          onGroupLabels={handleGroupLabels}
          onUngroupLabels={handleUngroupLabels}
          onGroupAllLabelsInScope={handleGroupAllLabelsInScope}
          onUngroupAllLabelsInScope={handleUngroupAllLabelsInScope}
          canPasteMeasurement={copiedMeasurementIds.length > 0}
          onCopyMeasurement={handleCopyMeasurement}
          onCopyMeasurements={handleCopyMeasurements}
          onPasteMeasurement={handlePasteMeasurement}
          onDuplicateMeasurement={handleDuplicateMeasurement}
          onDuplicateMeasurements={handleDuplicateMeasurements}
          showLabels={!tracingAiProposal && !manualAiMeasurement}
          interactionMode={tracingAiProposal || manualAiMeasurement ? "trace" : "standard"}
        />
        {overlaysEnabled ? (
          <DocumentTagEditorOverlay
            revisionId={planRevisionId}
            activeDetections={activePageDetections}
            archivedDetections={archivedPageDetections}
            getStyle={(detection) => {
              const color = getDetectionColor(detection);
              return {
                color,
                fillColor: hexToRgba(color, 0.18),
              };
            }}
            highlightedDetectionId={highlightedDetectionId}
            editMode={activeToolsetId === "tags"}
            onEditModeChange={(value) => {
              if (value) {
                setActiveTagTool(TAG_TOOL_IDS.add);
                setActiveToolsetId("tags");
                return;
              }
              setActiveTagTool(null);
              setActiveToolsetId((current) =>
                current === "tags" ? null : current,
              );
            }}
            showEditToggle={false}
            hidePanelWhenNotEditing
            showPanelOnlyWhenSelection
            allowCreate={false}
            tagOptions={tagPlacementOptions}
            selectedAddTagOptionId={selectedAddTagOptionId}
            placementMode={tagPlacementMode}
            onAction={recordActionLog}
            selectedDetectionIds={selectedTagDetectionIds}
            onSelectedDetectionIdsChange={(detectionIds) => {
              setSelectedTagDetectionIds(detectionIds);
              if (detectionIds.length > 0) {
                setSelectedMeasurementIds([]);
                setActiveMeasurementId(null);
              }
            }}
          />
        ) : null}
      </DocumentViewportHost>

      {overlaysEnabled &&
      activePageDetections.length === 0 &&
      archivedPageDetections.length === 0 &&
      !isPending ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
          <div className="pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm text-muted-foreground shadow-lg backdrop-blur">
            No indexed detections for the selected tags on this page.
          </div>
        </div>
      ) : null}
      {overlaysEnabled &&
      activePageDetections.length === 0 &&
      archivedPageDetections.length > 0 &&
      !isPending ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
          <div className="pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm text-muted-foreground shadow-lg backdrop-blur">
            No active detections remain on this page. Hidden tags are still available to restore.
          </div>
        </div>
      ) : null}
    </div>
  );
});

TakeoffResultsViewer.displayName = "TakeoffResultsViewer";
